'use client';

/**
 * StrategyPanel — Renders content based on strategy from COPILOT_MATRIX
 *
 * The strategy determines WHAT type of content to show.
 * The entityType determines WHICH entity to show it for.
 *
 * This is the "do what it's told" component - no logic, just dispatch.
 *
 * @layer presentation
 * @arch_component_type dispatcher
 */

import { Sparkles, Zap, MessageSquare, FileText, LayoutDashboard, Layers, Cpu, Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { CopilotStrategy } from '@/lib/api/types';

// ─────────────────────────────────────────────────────────────────────────────
// Details Components (strategy: entity_details)
// ─────────────────────────────────────────────────────────────────────────────
// Schema-driven Details - reads ENTITY_SCHEMA to determine sections
import { EntityDetailsLoader } from './details/entity-details-loader';

// ─────────────────────────────────────────────────────────────────────────────
// Synthesis Components (strategies: health, data_quality, rfq_analysis, etc.)
// ─────────────────────────────────────────────────────────────────────────────
import { ProjectSynthesis } from './insights/project-synthesis';
import { QualityPanel } from './insights/quality-panel';
import { RFQQuoteAnalysis } from './insights/rfq-quote-analysis';
import { VendorPerformance } from './insights/vendor-performance';
import { ProjectPerformance } from './insights/project-performance';

// ─────────────────────────────────────────────────────────────────────────────
// Actions Components (strategy: actions)
// ─────────────────────────────────────────────────────────────────────────────
import { ProjectActions } from './actions/project-actions';
import { RFQActions } from './actions/rfq-actions';
import { VendorActions } from './actions/vendor-actions';
import { ProductActions } from './actions/product-actions';
import { VendorResponseActions } from './actions/vendor-response-actions';

// ─────────────────────────────────────────────────────────────────────────────
// Chat Components (strategy: chat) - Unified BFF-driven messages + compose
// ─────────────────────────────────────────────────────────────────────────────
import { ChatPanel } from './chat-panel';

// ─────────────────────────────────────────────────────────────────────────────
// Docs Components (strategy: docs) - Documentation Q&A
// ─────────────────────────────────────────────────────────────────────────────
import { DocsPanel } from './insights/docs-panel';

// ─────────────────────────────────────────────────────────────────────────────
// Architecture Components (strategy: arch) - Architecture entity details
// ─────────────────────────────────────────────────────────────────────────────
import { ArchDetailsPanel } from './insights/arch-details-panel';

// ─────────────────────────────────────────────────────────────────────────────
// Agent Components (strategy: agent, agent_history, glass_box) - Simulation panels
// ─────────────────────────────────────────────────────────────────────────────
import { AgentApprovalPanel } from './insights/agent-approval-panel';
import { AgentHistoryPanel } from './insights/agent-history-panel';
import { AgentFlowWidget } from '../widgets/agent-flow-widget';

// ─────────────────────────────────────────────────────────────────────────────
// Testing Components (strategy: test_dashboard, test_details, test_cases, etc.)
// ─────────────────────────────────────────────────────────────────────────────
import { TestDashboardWidget } from '../widgets/test-dashboard-widget';
import { TestCaseDetailsLoader } from './details/test-case-details-loader';
import { TestCasesPanel } from './insights/test-cases-panel';
import { TestArchLinksPanel } from './insights/test-arch-links-panel';
import { TestGlassBoxPanel, ConversationGlassBoxPanel } from './insights/test-glass-box-panel';
import { ExtractionGlassBoxPanel } from './insights/extraction-glass-box-panel';

// RfiChatPanel kept for backward compat — routes to unified ChatPanel
import { RfiDetailsPanel } from './rfi-details-panel';
import { RfiAnalysisPanel } from './rfi-analysis-panel';
import { CostPlanDetailsPanel } from './costplan-details-panel';
import { ProductWishesPanel } from './insights/product-wishes-panel';
import { DiscoveryCostPanel } from './insights/discovery-cost-panel';

// ─────────────────────────────────────────────────────────────────────────────
// CC Details (strategy: cc_details) — schema-driven via DetailsPanel
// ─────────────────────────────────────────────────────────────────────────────
import { DetailsPanel } from './details/details-panel';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';


interface StrategyPanelProps {
  strategy: CopilotStrategy | null;
  mode: string;
  entityType: string | null;
  entityId: string | null;
  projectId?: string | null;
}

export function StrategyPanel({
  strategy,
  mode,
  entityType,
  entityId,
  projectId,
}: StrategyPanelProps) {
  // No strategy = welcome/empty state
  if (!strategy) {
    return <WelcomePanel mode={mode} />;
  }

  switch (strategy) {
    // ─────────────────────────────────────────────────────────────────────────
    // ENTITY_DETAILS: Schema-driven entity details from graph
    // ─────────────────────────────────────────────────────────────────────────
    case 'entity_details':
      return renderDetails(entityType, entityId, projectId);

    // ─────────────────────────────────────────────────────────────────────────
    // SUMMARY: Mode-level summary (no entity)
    // ─────────────────────────────────────────────────────────────────────────
    case 'summary':
      return <ModeSummary mode={mode} />;

    // ─────────────────────────────────────────────────────────────────────────
    // SYNTHESIS: LLM analysis strategies
    // ─────────────────────────────────────────────────────────────────────────
    case 'health':
      return renderHealthSynthesis(entityType, entityId, projectId);

    case 'data_quality':
      return <QualityPanel nodeId={entityId ?? ''} projectId={projectId} />;

    case 'rfq_analysis':
      return entityId ? <RFQQuoteAnalysis rfqId={entityId} /> : <EmptyState icon={Sparkles} message="Select an RFQ" />;

    case 'vendor_perf':
      return entityId ? <VendorPerformance vendorId={entityId} /> : <EmptyState icon={Sparkles} message="Select a vendor" />;

    case 'project_perf':
      return entityId ? <ProjectPerformance projectId={entityId} /> : <EmptyState icon={Sparkles} message="Select a project" />;

    case 'response_compare':
      // TODO: Implement response comparison component
      return <EmptyState icon={Sparkles} message="Response comparison coming soon" />;

    // ─────────────────────────────────────────────────────────────────────────
    // ACTIONS: Entity-specific action buttons
    // ─────────────────────────────────────────────────────────────────────────
    case 'actions':
      return renderActions(entityType, entityId, projectId);

    // ─────────────────────────────────────────────────────────────────────────
    // CHAT: Unified BFF-driven messages + compose
    // ─────────────────────────────────────────────────────────────────────────
    case 'chat':
      return <ChatPanel entityType={entityType} entityId={entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // DOCS: Documentation Q&A display
    // ─────────────────────────────────────────────────────────────────────────
    case 'docs':
      return <DocsPanel />;

    // ─────────────────────────────────────────────────────────────────────────
    // ARCH: Architecture entity details from Neo4j graph
    // ─────────────────────────────────────────────────────────────────────────
    case 'arch':
      return <ArchDetailsPanel />;

    // ─────────────────────────────────────────────────────────────────────────
    // AGENT: Agent simulation approval panel
    // ─────────────────────────────────────────────────────────────────────────
    case 'agent':
      return <AgentApprovalPanel />;

    // ─────────────────────────────────────────────────────────────────────────
    // AGENT_HISTORY / HISTORY: Agent simulation history list
    // ─────────────────────────────────────────────────────────────────────────
    case 'agent_history':
    case 'history':
      return <AgentHistoryPanel />;

    // ─────────────────────────────────────────────────────────────────────────
    // GLASS_BOX: Agent simulation step visualization (audit trail)
    // ─────────────────────────────────────────────────────────────────────────
    case 'glass_box':
      return <AgentFlowWidget />;

    // ─────────────────────────────────────────────────────────────────────────
    // TEST_DASHBOARD: Test statistics and pass rates
    // ─────────────────────────────────────────────────────────────────────────
    case 'test_dashboard':
      return <TestDashboardWidget />;

    // ─────────────────────────────────────────────────────────────────────────
    // CC_DETAILS: Complexity hotspot details — schema-driven (no API)
    // ─────────────────────────────────────────────────────────────────────────
    case 'cc_details':
      return <CcDetailsPanel />;

    // ─────────────────────────────────────────────────────────────────────────
    // TEST_DETAILS: Schema-driven test case details (same pattern as odoo)
    // ─────────────────────────────────────────────────────────────────────────
    case 'test_details':
      return entityId ? (
        <TestCaseDetailsLoader entityId={entityId} />
      ) : (
        <EmptyState icon={FileText} message="Select a test case" />
      );

    // ─────────────────────────────────────────────────────────────────────────
    // TEST_CASES: Copilot test cases list with play buttons
    // ─────────────────────────────────────────────────────────────────────────
    case 'test_cases':
      return <TestCasesPanel entityType={entityType} entityId={entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // TEST_ARCH_LINKS: Architecture packages linked to a test case
    // ─────────────────────────────────────────────────────────────────────────
    case 'test_arch_links':
      return <TestArchLinksPanel entityId={entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // TEST_GLASS_BOX: OODA-L audit trail from test execution
    // ─────────────────────────────────────────────────────────────────────────
    case 'test_glass_box':
      return <TestGlassBoxPanel entityId={entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // RFI_DETAILS: BFF-driven entity details for RFI mode
    // ─────────────────────────────────────────────────────────────────────────
    case 'rfi_details':
      return <RfiDetailsPanel entityType={entityType} entityId={entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // RFI_ANALYSIS: BFF-driven RFQ quote analysis
    // ─────────────────────────────────────────────────────────────────────────
    case 'rfi_analysis':
      return <RfiAnalysisPanel entityId={entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // COSTPLAN_DETAILS: CostPlan (MCP) details from V2 data gateway
    // ─────────────────────────────────────────────────────────────────────────
    case 'costplan_details':
      return <CostPlanDetailsPanel entityId={entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // PRODUCT_WISHES: Buyer product wishes for a project
    // ─────────────────────────────────────────────────────────────────────────
    case 'product_wishes':
      return <ProductWishesPanel projectId={projectId ?? entityId} />;

    // ─────────────────────────────────────────────────────────────────────────
    // EXTRACTION_GLASS_BOX: OODA-L audit trail from DI extraction
    // ─────────────────────────────────────────────────────────────────────────
    case 'extraction_glass_box':
      return <ExtractionGlassBoxPanel />;

    // ─────────────────────────────────────────────────────────────────────────
    // DISCOVERY_COST: Token usage & cost dashboard
    // ─────────────────────────────────────────────────────────────────────────
    case 'discovery_cost':
      return <DiscoveryCostPanel />;

    case 'di_alignment':
      return <DIAlignmentPanel entityId={entityId} />;

    default:
      return <EmptyState icon={FileText} message={`Unknown strategy: ${strategy}`} />;
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// RENDER HELPERS — Dispatch based on entityType
// ═══════════════════════════════════════════════════════════════════════════════

function renderDetails(
  entityType: string | null,
  entityId: string | null,
  projectId?: string | null,
) {
  // Schema-driven: EntityDetailsLoader reads ENTITY_SCHEMA to determine sections
  return (
    <EntityDetailsLoader
      entityType={entityType ?? 'unknown'}
      entityId={entityId ?? ''}
      projectId={projectId}
    />
  );
}

function renderHealthSynthesis(
  entityType: string | null,
  entityId: string | null,
  projectId?: string | null,
) {
  // Use projectId from context if available, otherwise use entityId if it's a project
  const targetProjectId = projectId ?? (entityType === 'project' ? entityId : null);

  if (!targetProjectId) {
    return <EmptyState icon={Sparkles} message="Select a project to see insights" />;
  }

  return <ProjectSynthesis projectId={targetProjectId} />;
}

function renderActions(
  entityType: string | null,
  entityId: string | null,
  projectId?: string | null,
) {
  if (!entityId) {
    return <EmptyState icon={Zap} message="Select an item to see actions" />;
  }

  switch (entityType) {
    case 'project':
      return <ProjectActions projectId={entityId} />;
    case 'rfq':
      return <RFQActions rfqId={entityId} projectId={projectId} />;
    case 'vendor':
      return <VendorActions vendorId={entityId} />;
    case 'product':
    case 'lineitem':
      return <ProductActions nodeId={entityId} projectId={projectId} />;
    case 'vendor_response':
      return <VendorResponseActions responseCdk={entityId} projectId={projectId} />;
    default:
      return <EmptyState icon={Zap} message={`No actions for ${entityType ?? 'unknown'}`} />;
  }
}

const SEVERITY_BADGES: Record<string, string> = {
  critical: 'bg-destructive/20 text-destructive',
  high: 'bg-warning/20 text-warning',
  moderate: 'bg-primary/20 text-primary',
};

function CcDetailsPanel() {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);

  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <Cpu className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm font-medium">Complexity Details</p>
        <p className="text-xs mt-1">Select a function or module from the hotspots table</p>
      </div>
    );
  }

  const nodeType = selectedNode.type ?? 'unknown';
  const props = (selectedNode.properties ?? {}) as Record<string, unknown>;

  return (
    <div className="p-3 space-y-4">
      <CcEntityHeader selectedNode={selectedNode} />
      <DetailsPanel
        entityType={nodeType}
        entityId={selectedNode.id ?? ''}
        data={props}
      />
    </div>
  );
}

function CcEntityHeader({ selectedNode }: { selectedNode: { type: string; label: string; properties?: Record<string, unknown> } }) {
  const props = selectedNode.properties ?? {};

  if (selectedNode.type === 'cc_module') {
    const displayName = (props.display_name ?? props.package ?? selectedNode.label) as string;
    const packageName = props.package as string | undefined;

    return (
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-primary/20 text-primary">
          <Layers className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{displayName}</h3>
          {packageName && packageName !== displayName && (
            <p className="text-xs text-muted-foreground font-mono mt-0.5">{packageName}</p>
          )}
        </div>
      </div>
    );
  }

  if (selectedNode.type === 'cc_function') {
    const functionName = (props.function_name ?? props.name ?? selectedNode.label) as string;
    const severity = (props.severity as string) ?? 'moderate';
    const badgeColor = SEVERITY_BADGES[severity] ?? SEVERITY_BADGES.moderate;

    return (
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${badgeColor}`}>
          <Cpu className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold font-mono truncate">{functionName}</h3>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs capitalize ${badgeColor}`}>
            {severity}
          </span>
        </div>
      </div>
    );
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function WelcomePanel({ mode }: { mode: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
      <LayoutDashboard className="h-8 w-8 mb-2 opacity-50" />
      <p className="text-sm font-medium capitalize">{mode} Mode</p>
      <p className="text-xs mt-1">Select an item from the canvas to see details</p>
    </div>
  );
}

function ModeSummary({ mode }: { mode: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
      <LayoutDashboard className="h-8 w-8 mb-2 opacity-50" />
      <p className="text-sm font-medium capitalize">{mode} Overview</p>
      <p className="text-xs mt-1">Summary view coming soon</p>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  message
}: {
  icon: React.ComponentType<{ className?: string }>;
  message: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
      <Icon className="h-8 w-8 mb-2 opacity-50" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

/** DI Alignment — LLM-narrated attribution summary. */
function DIAlignmentPanel({ entityId }: { entityId: string | null }) {
  const strategy = useCapabilityStore((s) => s.strategyName('discovery', 'alignment_summary'));
  const { data, isLoading } = useStrategyData<{
    summary: string;
    intent_name: string;
    project_name: string;
    influenced_pct: number;
    rfq_products: number;
    missed_count: number;
    total_potential_saving: number;
  }>(
    strategy ?? 'di_alignment_summary',
    entityId ? { intent_sid: entityId } : {},
    !!entityId,
  );

  const result = data?.data;

  if (!entityId) {
    return <EmptyState icon={Sparkles} message="Select a style guide to see alignment" />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Analysing alignment...
      </div>
    );
  }

  if (!result?.summary) {
    return <EmptyState icon={Sparkles} message="No alignment data available" />;
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold mb-1">{result.intent_name}</h3>
        <p className="text-xs text-muted-foreground">{result.project_name}</p>
      </div>

      <p className="text-sm leading-relaxed">{result.summary}</p>

      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
        <div className="text-center">
          <p className="text-lg font-bold">{result.influenced_pct}%</p>
          <p className="text-[10px] text-muted-foreground">DI-influenced</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold">{result.rfq_products}</p>
          <p className="text-[10px] text-muted-foreground">Products</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold">{result.missed_count}</p>
          <p className="text-[10px] text-muted-foreground">Missed</p>
        </div>
      </div>

      {result.total_potential_saving > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
          <p className="text-sm font-medium text-amber-600">
            Potential saving: ${result.total_potential_saving.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}

